import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faLocationPin, faWarning } from '@fortawesome/pro-regular-svg-icons';
import { formatSigningAppointmentDateTime } from '../../../../../types/signingAppointment/ISigningAppointmentResponse';
import { propertyAddressToString } from '../../../../../types/address/IPropertyAddressData';
import React, { useMemo } from 'react';
import { CreateSigningAppointmentFormData } from '../04_confirmationStep/convertToCreateSigningAppointmentRequest';
import { Show } from 'components/shared/Show';
import { convertToPropertyAddressData } from 'utilities/scheduling/address/ConvertToPropertyAddressData';
import { getDssDateTimeInfo } from '../../utilities/getDssDateTimeInfo';

export const DateAndAddressInfoSection: React.FC<{ formData: CreateSigningAppointmentFormData }> = ({ formData }) => {
  const {
    appointmentDate,
    appointmentTime,
    availableStartDate,
    availableEndDate,
    address,
    signerWillProvideLocation,
    signerSelectsFromRange,
    dssDate,
    dssDateRangeEnd,
    dssDateRangeStart,
    dssStartTime,
    dssEndTime,
    isDssDateRange,
  } = formData;

  const addressValidationObject = address ? convertToPropertyAddressData({ ...address }) : null;
  const areMissingFields = !!addressValidationObject?.missingFields?.length;
  const isEmptyAddress = !!addressValidationObject?.isEmpty || !address;
  const getAddress = useMemo(() => {
    if (signerWillProvideLocation) {
      return 'Location Pending Selection';
    }

    if (!isEmptyAddress && addressValidationObject?.address && !signerWillProvideLocation) {
      return propertyAddressToString(addressValidationObject?.address);
    }
    return 'Location Pending Selection';
  }, [addressValidationObject?.address, isEmptyAddress, signerWillProvideLocation]);
  const getDateTime = useMemo(() => {
    if (!signerSelectsFromRange) {
      return formatSigningAppointmentDateTime(
        appointmentDate,
        appointmentTime,
        undefined,
        availableStartDate ?? undefined,
        availableEndDate ?? undefined
      );
    }
    return getDssDateTimeInfo({
      isDssDateRange,
      dssDate,
      dssDateRangeStart,
      dssDateRangeEnd,
      dssStartTime,
      dssEndTime,
    });
  }, [
    appointmentDate,
    appointmentTime,
    availableEndDate,
    availableStartDate,
    dssDate,
    dssDateRangeEnd,
    dssDateRangeStart,
    dssEndTime,
    dssStartTime,
    isDssDateRange,
    signerSelectsFromRange,
  ]);
  return (
    <section className={'mb-4'}>
      <div className="mb-2 flex" data-test-id="new-event-date">
        <FontAwesomeIcon className="mr-2 mt-1" icon={faCalendar} />
        <p className="text-content-default-content-text text-base font-medium leading-5">{getDateTime}</p>
      </div>
      <div className="flex" data-test-id="new-event-address">
        <Show
          when={areMissingFields && !isEmptyAddress && !signerWillProvideLocation}
          fallback={
            <>
              <FontAwesomeIcon className="mr-2 mt-1" icon={faLocationPin} />
              <p className="text-content-default-content-text text-sm leading-5">{getAddress}</p>
            </>
          }
        >
          <>
            <FontAwesomeIcon className="mr-2 mt-1" icon={faWarning} />
            <p className="text-content-default-content-text text-sm leading-5">
              Please review address, some fields may be missing
            </p>
          </>
        </Show>
      </div>
    </section>
  );
};
